import { Stack, createStyles } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';
import Trans from 'next-translate/Trans';

import { Button } from '~/domains/design-system/Button';
import { Text } from '~/domains/design-system/Text';
import { Title } from '~/domains/design-system/Title';
import { IconAlertCircle } from '~/domains/design-system/icons/IconAlertCircle';
import dt from '~/testing';

const dtIds = dt.components.shared.auth.signUpVersionTwo.existingAccountModal;

const useStyles = createStyles((theme) => ({
  container: {
    padding: /* eslint-disable-line local-rules/use-theme-tokens */ '16px 24px 32px',
    alignItems: 'center',
    textAlign: 'center',
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 8px',
    backgroundColor: theme.colors.gray[1],
    borderRadius: '50%',
    width: 56,
    height: 56,
  },
  title: {
    marginBottom: /* eslint-disable-line local-rules/use-theme-tokens */ 10,
  },
  content: {
    maxWidth: 420,
  },
  actions: {
    width: '100%',
    maxWidth: 420,
    marginTop: theme.other.spacing._12,
  },
}));

type ExistingAccountContentProps = {
  email: string;
  onLogIn: () => void;
  onUseDifferentEmail: () => void;
};

export default function ExistingAccountContent({
  email,
  onLogIn,
  onUseDifferentEmail,
}: ExistingAccountContentProps) {
  const { t } = useTranslation('auth');
  const { classes, theme } = useStyles();

  return (
    <Stack className={classes.container}>
      <div className={classes.content}>
        <div className={classes.iconContainer}>
          <IconAlertCircle size={28} color={theme.colors.dark[9]} />
        </div>
        <Title
          data-test-id={dtIds.title}
          variant="subhead-x-large"
          weight={700}
          className={classes.title}
        >
          {t('signUp.existingAccount.title')}
        </Title>
        <Text data-test-id={dtIds.description} variant="body-large" color="gray.7">
          <Trans
            i18nKey="auth:signUp.existingAccount.copy"
            values={{ email }}
            components={{ bold: <strong /> }}
          />
        </Text>
      </div>
      <Stack className={classes.actions}>
        <Button
          data-test-id={dtIds.logInButton}
          variant="primary-fill"
          size="large-icon"
          onClick={onLogIn}
        >
          {t('signUp.existingAccount.logIn')}
        </Button>
        <Button
          data-test-id={dtIds.useDifferentEmailButton}
          variant="tertiary-fill"
          onClick={onUseDifferentEmail}
        >
          {t('signUp.existingAccount.useDifferentEmail')}
        </Button>
      </Stack>
    </Stack>
  );
}
